/**
 * Removes null, undefined, empty strings, empty arrays, and empty objects from an object.
 * Returns a new object containing only properties with non-empty values.
 *
 * @param obj - The input object to clean
 * @returns A new object with empty/null/undefined values removed
 */
export function removeEmptyValues<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    // Skip null and undefined
    if (value === null || value === undefined) {
      return acc;
    }

    // Skip empty string
    if (value === '') {
      return acc;
    }

    // Skip empty array
    if (Array.isArray(value) && value.length === 0) {
      return acc;
    }

    // Skip empty object (but not Date, RegExp, etc.)
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !(value instanceof Date) &&
      !(value instanceof RegExp) &&
      Object.keys(value).length === 0
    ) {
      return acc;
    }

    // For nested objects, recursively clean them
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const cleaned = removeEmptyValues(value as Record<string, any>);

      if (Object.keys(cleaned).length > 0) {
        (acc as any)[key] = cleaned;
      }

      return acc;
    }

    // Keep all other values
    (acc as any)[key] = value;

    return acc;
  }, {} as Partial<T>);
}
